const featureCards = [
  {
    title: "Lead Management",
    description:
      "Centralize lead capture, score intelligently, automate nurturing, track in real-time, assign flexibly, collaborate seamlessly, and gain insights.",
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 14a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm-7 7a7 7 0 0 1 14 0"
      />
    ),
  },
  {
    title: "Customer Analytics",
    description: `Use customer analytics to propel growth, improve engagement, and accomplish your company's objectives.`,
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 14h4v6H4zm6-5h4v11h-4zm6-4h4v15h-4z"
      />
    ),
  },
  {
    title: "Customer Support",
    description:
      "You can browse and maximize your CRM experience with confidence knowing that expert assistance is available whenever need.",
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 18 10 12l4 4 6-8M4 6h16"
      />
    ),
  },
  {
    title: "Seamless Integration",
    description:
      "Simplify your operations and integrate your business ecosystem to increase production and efficiency.",
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 19V9m7 10V5m7 14v-7"
      />
    ),
  },
  {
    title: "Lead Journey Tracking",
    description:
      "Track and manage leads from first contact to conversion with a detailed timeline.",
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 20a3 3 0 0 0-6 0m6 0a3 3 0 0 1 6 0M7 20a3 3 0 0 1 6 0M7 20H3m4 0h10M9 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm6 2a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
      />
    ),
  },
  {
    title: "Effortless Communication",
    description:
      "Connect seamlessly via WhatsApp chats or direct emails from the CRM.",
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 21c4.97-2.846 8-6.79 8-11a4 4 0 0 0-7-2.646A4 4 0 0 0 6 10c0 4.21 3.03 8.154 8 11Z"
      />
    ),
  },
];

const valueCards = [
  {
    title: "Fast Implementation",
    description:
      "Launch quickly with intuitive workflows that teams adopt without heavy training cycles.",
  },
  {
    title: "Built for Scale",
    description:
      "Support growing teams, larger data volumes, and more advanced lifecycle automation with ease.",
  },
  {
    title: "Revenue-Focused Design",
    description:
      "Every dashboard, reminder, and report is shaped to help you close and retain more business.",
  },
];

function IconShell({ children }) {
  return (
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0a2540] to-[#0b5ed7] text-white shadow-lg shadow-blue-200">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        className="h-7 w-7"
      >
        {children}
      </svg>
    </div>
  );
}

function Features() {
  return (
    <section
      id="features"
      className="relative overflow-hidden pt-10 pb-20 lg:pt-12 lg:pb-24"
    >
      <div className="absolute left-0 top-16 h-48 w-48 rounded-full bg-blue-100 blur-3xl" />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-lg font-bold uppercase tracking-[0.3em] text-[#0b5ed7]">
            Features
          </p>
          <h2 className="mt-4 font-display text-4xl font-extrabold text-[#0a2540] md:text-4xl">
            Everything your team needs to keep customer relationships moving
          </h2>
          <p className="mt-5 text-lg leading-8 text-slate-600">
            Opt4CRM combines visibility, automation, and collaboration into a
            single SaaS experience built for consistent growth.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {featureCards.map((feature) => (
            <article
              key={feature.title}
              className="group rounded-[28px] border border-slate-200/80 bg-white p-7 shadow-lg shadow-slate-200/50 transition duration-300 hover:-translate-y-2 hover:border-[#dbeafe] hover:shadow-2xl hover:shadow-[0_25px_60px_rgba(11,94,215,0.12)]"
            >
              <IconShell>{feature.icon}</IconShell>
              <h3 className="mt-6 text-xl font-extrabold text-[#0a2540]">
                {feature.title}
              </h3>
              <p className="mt-3 leading-7 text-slate-600">
                {feature.description}
              </p>
              <div className="mt-6 flex items-center gap-2 text-sm font-bold text-[#0b5ed7]">
                Explore feature
                <span className="transition group-hover:translate-x-1">→</span>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-24 grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-[#0b5ed7]">
              Why Choose Us
            </p>
            <h3 className="mt-4 font-display text-4xl font-extrabold text-[#0a2540] md:text-[2.30rem]">
              Transform Your Business with Our CRM
            </h3>
            <p className="mt-5 max-w-xl text-lg leading-8 text-slate-600">
              From first lead to long-term retention, Opt4CRM keeps your team
              aligned with fewer manual steps and better customer context.
            </p>
          </div>

          <div className="grid gap-5">
            {valueCards.map((card, index) => (
              <article
                key={card.title}
                className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0a2540] text-lg font-extrabold text-white">
                    0{index + 1}
                  </div>
                  <div>
                    <h4 className="text-xl font-extrabold text-[#0a2540]">
                      {card.title}
                    </h4>
                    <p className="mt-2 leading-7 text-slate-600">
                      {card.description}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Features;
